<template>
    <div class="sap-rewrite-queue">
        <div class="card mb-2">
            <div class="card-body py-2">
                <div class="d-flex flex-wrap align-items-center">
                    <span class="font-weight-bold mr-3">工时回写队列(PCNF / CNF)</span>
                    <b-badge :variant="sapClosedNow ? 'warning' : 'success'" class="mr-3">
                        {{ sapClosedNow ? "SAP关闭时段中, 自动回写暂停" : "自动回写运行中" }}
                    </b-badge>
                    <span class="small text-muted">
                        补回写起始时间: <span class="text-body">{{ formatTime(since) || "-" }}</span>
                    </span>
                </div>
                <div class="small text-muted mt-1">
                    同一工单按需回写时间顺序回写(同一时刻PCNF先于CNF); 工单内有一条回写失败或回写中, 该工单后续记录全部暂停,
                    需人工「重试本工单」或「标记已处理」后才继续。早于起始时间的等待回写记录不会自动回写, 只能人工处理。
                    「回写中」超过30分钟仍未结束会标为「结果未知」(多为调用SAP途中服务重启), 不会自动重发, 请先在SAP中核实。
                </div>
            </div>
        </div>

        <div class="card mb-2">
            <div class="card-body py-2">
                <b-form inline @submit.prevent="search(1)">
                    <label class="mr-1 small">类型</label>
                    <b-form-select v-model="filter.type" :options="typeOptions" size="sm" class="mr-3"></b-form-select>
                    <label class="mr-1 small">工单号</label>
                    <b-form-input v-model="filter.orderCode" size="sm" class="mr-3" placeholder="支持模糊查询"></b-form-input>
                    <label class="mr-1 small">需回写时间</label>
                    <b-form-input v-model="filter.dateFrom" type="date" size="sm"></b-form-input>
                    <span class="mx-1">~</span>
                    <b-form-input v-model="filter.dateTo" type="date" size="sm" class="mr-3"></b-form-input>
                    <label class="mr-2 small">状态</label>
                    <b-form-checkbox-group v-model="filter.statuses" :options="statusOptions" size="sm" class="mr-3">
                    </b-form-checkbox-group>
                    <b-button type="submit" variant="primary" size="sm" class="mr-1" :disabled="loading">
                        <i class="fa fa-fw fa-search"></i>查询
                    </b-button>
                    <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="reset">
                        <i class="fa fa-fw fa-undo"></i>重置
                    </b-button>
                </b-form>
            </div>
        </div>

        <div class="card">
            <div class="card-body">
                <b-overlay :show="loading" rounded="sm">
                    <b-table :items="items" :fields="fields" small hover bordered responsive show-empty
                             empty-text="暂无记录" class="mb-2">
                        <template #cell(type)="{item}">
                            <b-badge :variant="item.type === 'CNF' ? 'primary' : 'info'">{{ item.type }}</b-badge>
                        </template>
                        <template #cell(rewriteTime)="{item}">{{ formatTime(item.rewriteTime) }}</template>
                        <template #cell(operation)="{item}">
                            {{ item.operationSeq }}
                            <span v-if="item.operationDescription" class="text-muted small"
                                  :title="item.operationDescription">{{ truncate(item.operationDescription, 200) }}</span>
                        </template>
                        <template #cell(workHours)="{item}">{{ item.workHours }}</template>
                        <template #cell(sapRewriteStatus)="{item}">
                            <b-badge :variant="statusVariant(item.sapRewriteStatus)">{{ statusText(item.sapRewriteStatus) }}</b-badge>
                            <b-badge v-if="item.history" variant="secondary" class="ml-1"
                                     title="早于补回写起始时间, 不会自动回写">历史</b-badge>
                            <b-badge v-if="item.blocked" variant="warning" class="ml-1"
                                     title="所在工单有回写失败或回写中的记录, 处理完之前不会自动回写">阻塞中</b-badge>
                            <b-badge v-if="item.stale" variant="danger" class="ml-1"
                                     title="回写中超过30分钟仍未结束, SAP是否已确认未知, 请先在SAP中核实">结果未知</b-badge>
                        </template>
                        <template #cell(sapRewriteMessage)="{item}">
                            <span class="message-cell small" :title="item.sapRewriteMessage">{{ item.sapRewriteMessage }}</span>
                        </template>
                        <template #cell(actions)="{item}">
                            <span v-if="isRunning(item)" class="small text-muted">
                                <i class="fa fa-fw fa-spinner fa-spin"></i>正在回写
                            </span>
                            <div v-else-if="isPending(item)" class="d-flex flex-nowrap">
                                <b-button variant="outline-primary" size="sm" class="mr-1"
                                          :disabled="busyHeaderId !== null" @click="retryOrder(item)">
                                    <i class="fa fa-fw" :class="busyHeaderId === item.prdPpHeaderId ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>重试本工单
                                </b-button>
                                <b-button variant="outline-secondary" size="sm"
                                          :disabled="busyHeaderId !== null" @click="markHandled(item)">
                                    <i class="fa fa-fw fa-check"></i>标记已处理
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </b-overlay>
                <div class="d-flex align-items-center">
                    <span class="small text-muted mr-3">共 {{ total }} 条</span>
                    <b-pagination v-model="page" :total-rows="total" :per-page="size" size="sm" class="mb-0"
                                  @change="search"></b-pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import dayjs from "dayjs";
    import commonViewsUtil from "@/utils/commonViewsUtil";

    const WAIT = 4;
    const FAILED = 2;
    const PROCESSING = 5;
    const STATUS = {
        1: {text: "回写成功", variant: "success"},
        2: {text: "回写失败", variant: "danger"},
        3: {text: "跳过回写", variant: "secondary"},
        4: {text: "等待回写", variant: "info"},
        5: {text: "回写中", variant: "warning"}
    };

    function defaultFilter() {
        return {type: null, orderCode: "", dateFrom: "", dateTo: "", statuses: [WAIT, FAILED, PROCESSING]};
    }

    export default {
        name: "sithMesPrdSapRewriteQueue",
        data() {
            return {
                filter: defaultFilter(),
                items: [],
                total: 0,
                page: 1,
                size: 20,
                since: null,
                sapClosedNow: false,
                loading: false,
                // 重试/标记进行中的工单; 非空时禁用全部操作按钮, 避免同时对多个工单发起回写
                busyHeaderId: null,
                typeOptions: [
                    {value: null, text: "全部"},
                    {value: "PCNF", text: "PCNF(下工)"},
                    {value: "CNF", text: "CNF(完工)"}
                ],
                statusOptions: [WAIT, FAILED, PROCESSING, 3, 1].map(code => ({value: code, text: STATUS[code].text})),
                fields: [
                    {key: "index", label: "#"},
                    {key: "type", label: "类型"},
                    {key: "orderCode", label: "工单号"},
                    {key: "operation", label: "工序"},
                    {key: "rewriteTime", label: "需回写时间", tdClass: "text-nowrap"},
                    {key: "workHours", label: "工时(h)"},
                    {key: "userName", label: "人员", tdClass: "text-nowrap"},
                    {key: "sapRewriteStatus", label: "回写状态"},
                    {key: "sapRewriteMessage", label: "回写信息"},
                    {key: "actions", label: "操作"}
                ]
            };
        },
        computed: {
            baseUrl() {
                return process.env.VUE_APP_SITHMES_PRD_URL + "/sithMesPrdSapRewrite";
            }
        },
        created() {
            this.search(1);
        },
        methods: {
            search(page) {
                this.page = page;
                this.loading = true;
                const data = {
                    type: this.filter.type,
                    orderCode: this.filter.orderCode,
                    statuses: this.filter.statuses,
                    rewriteTimeFrom: this.filter.dateFrom ? dayjs(this.filter.dateFrom).valueOf() : null,
                    // 截止日期含当天
                    rewriteTimeTo: this.filter.dateTo ? dayjs(this.filter.dateTo).add(1, "day").valueOf() : null,
                    page,
                    size: this.size
                };
                return this.$store.dispatch("api/post", {url: this.baseUrl + "/query", data})
                    .then(res => {
                        const body = res.data;
                        this.items = (body.list || []).map((item, i) => ({...item, index: (page - 1) * this.size + i + 1}));
                        this.total = body.total || 0;
                        this.since = body.since;
                        this.sapClosedNow = !!body.sapClosedNow;
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            reset() {
                this.filter = defaultFilter();
                this.search(1);
            },
            // 等待/失败/结果未知的回写中: 可人工重试或标记
            isPending(item) {
                return item.sapRewriteStatus === WAIT || item.sapRewriteStatus === FAILED
                    || (item.sapRewriteStatus === PROCESSING && item.stale);
            },
            // 未超时的回写中: 很可能正在调SAP, 不允许人工操作
            isRunning(item) {
                return item.sapRewriteStatus === PROCESSING && !item.stale;
            },
            statusText(code) {
                return (STATUS[code] && STATUS[code].text) || "-";
            },
            statusVariant(code) {
                return (STATUS[code] && STATUS[code].variant) || "light";
            },
            truncate(text, max) {
                return text.length > max ? text.slice(0, max) + "…" : text;
            },
            formatTime(value) {
                return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "";
            },
            confirm(title, lines) {
                const h = this.$createElement;
                const body = lines.map(line => h("p", {class: line.danger ? "text-danger mb-2" : "mb-2"}, line.text || line));
                return this.$bvModal.msgBoxConfirm(body, {
                    title,
                    size: "md",
                    okTitle: "确定",
                    cancelTitle: "取消",
                    centered: true
                });
            },
            retryOrder(item) {
                this.confirm("重试工单 " + item.orderCode, [
                    "将按需回写时间顺序, 依次回写该工单下所有「等待回写」「回写失败」「结果未知」的记录(含历史记录), 遇到失败即停止。",
                    {
                        text: "「回写失败」「结果未知」的记录可能已在SAP中确认过, 请先在SAP中核实, 已确认过的请改用「标记已处理」, 否则会重复确认。",
                        danger: true
                    }
                ]).then(ok => {
                    if (!ok) {
                        return;
                    }
                    this.busyHeaderId = item.prdPpHeaderId;
                    this.$store.dispatch("api/post", {url: this.baseUrl + "/retryOrder?prdPpHeaderId=" + item.prdPpHeaderId})
                        .then(res => {
                            const result = res.data;
                            if (result.failedId === null || result.failedId === undefined) {
                                commonViewsUtil.showSuccess(this, `工单 ${item.orderCode} 回写完成, 共 ${result.succeeded} 条`);
                            } else {
                                commonViewsUtil.showWarning(this, `工单 ${item.orderCode} 成功 ${result.succeeded}/${result.total} 条, `
                                    + `停在 ${result.failedType}#${result.failedId}: ${result.failedMessage || ""}`);
                            }
                        })
                        .catch(() => {
                        })
                        .finally(() => {
                            this.busyHeaderId = null;
                            this.search(this.page);
                        });
                });
            },
            markHandled(item) {
                this.confirm("标记已处理", [
                    `${item.type} 工单 ${item.orderCode} 工序 ${item.operationSeq}(需回写时间 ${this.formatTime(item.rewriteTime)})`,
                    "确认已在SAP中手工处理或无需回写? 标记后状态改为「跳过回写」, 不会再回写SAP, 该工单后续的回写将继续执行。"
                ]).then(ok => {
                    if (!ok) {
                        return;
                    }
                    this.busyHeaderId = item.prdPpHeaderId;
                    this.$store.dispatch("api/post", {
                        url: this.baseUrl + "/markHandled?type=" + item.type + "&id=" + item.id
                    })
                        .then(() => commonViewsUtil.showSuccess(this, "已标记为跳过回写"))
                        .catch(() => {
                        })
                        .finally(() => {
                            this.busyHeaderId = null;
                            this.search(this.page);
                        });
                });
            }
        }
    };
</script>

<style scoped lang="scss">
    .sap-rewrite-queue {
        padding: 16px;

        ::v-deep thead th {
            white-space: nowrap;
        }

        .message-cell {
            display: inline-block;
            max-width: 320px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: middle;
        }
    }
</style>
